import {
  Alert,
  Box,
  Card,
  Link,
  Modal,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { ReactComponent as BGLeft } from "../../../assets/Login-BgLeft.svg";
import { ReactComponent as BGRight } from "../../../assets/Login-BgRight.svg";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import axiosJWT from "../../../services/axios";
import { MacsAuthenticator } from "../../../hooks/useMacsAuthenticator";
import { useLocation } from "react-router-dom";
import {
  ICanvasTokenRedirectParams,
  Class,
  LtiAssignment,
  User,
  InitialSyncResponse,
  CanvasSyncErrorItem,
  CanvasSyncErrorCode,
} from "@backend-types";
import { LoadingButton } from "@mui/lab";
import { DataGrid } from "@mui/x-data-grid";
import { IWebsocketContext, WebsocketContext } from "../../../context/WebSocketContext";
import { ErrorToast } from "../../v2/utils/Toast";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    flexGrow: 1,
    backgroundColor: "#303030",
    position: "relative",
    overflow: "hidden",
  },
  center: {
    display: "flex",
    justifyContent: "center",
    overflow: "auto",
  },
  container: {
    width: "100%",
    maxWidth: 1366,
    height: "100%",
    display: "flex",
    justifyContent: "space-between",
    padding: "5rem 5rem",
    alignItems: "center",
    [theme.breakpoints.down("lg")]: {
      padding: "1rem 1rem 0rem 1rem",
    },
  },
  backgroundImage: {
    height: "100%",
    width: "auto",
    zIndex: 0,
    position: "absolute",
  },
  header: {
    fontWeight: 700,
    fontSize: 36,
    color: "#ffffff",
  },
  subHeader: {
    fontWeight: 400,
    fontSize: 16,
    marginTop: "1rem",
    marginBottom: "2rem",
    maxWidth: 582,
    color: "#ffffff",
  },
  thankYouNote: {
    fontWeight: 600,
    fontSize: 24,
    color: "#191919",
  },
  thankYouSubNote: {
    fontWeight: 500,
    fontSize: 16,
    marginTop: "1.5rem",
    maxWidth: 582,
    color: "#747474",
  },
  formCardCentered: {
    backgroundColor: "#FFFFFF",
    color: "#000000E6",
    borderRadius: "8px",
    padding: "2rem",
    transition: "height 200ms ease-in-out",
    zIndex: 1,
  },
  fieldsContainer: {
    display: "flex",
    justifyContent: "space-between",
  },
  contentContainer: {
    zIndex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    width: "100%",
    height: "100%",
  },
  headerContainer: {
    maxWidth: "480px",
    height: "454px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    [theme.breakpoints.down("lg")]: {
      marginLeft: "0rem",
    },
  },
  pad16: {
    padding: "0px 0px 16px 0px",
  },
  pad24: {
    padding: "0px 0px 24px 0px",
    marginLeft: "-12px",
  },
  tosStyle: {
    marginBottom: "16px",
    "& button": {
      color: "inherit",
    },
  },
  link: {
    textDecoration: "underline",
    color: "inherit",
    cursor: "pointer",
    background: "none",
    border: "none",
  },
}));

function instanceOfRedirect(object: any): object is ICanvasTokenRedirectParams {
  return object && typeof object === "object" && "redirect_uri" in object;
}

function instanceOfInitialSyncResponse(object: any): object is InitialSyncResponse {
  return (
    object &&
    typeof object === "object" &&
    "provisioningStatus" in object &&
    "provisionedClasses" in object &&
    "provisionedAssignments" in object &&
    "provisionedExams" in object
  );
}

export interface ICanvasSyncResult {
  provisioningStatus: "success" | "failure" | "inprogress";
  provisionedClasses: Class[];
  provisionedAssignments: LtiAssignment[];
  provisionedExams: LtiAssignment[];
  usersToSync: number; // we will fill this from Stage 2 (activeUsersCount)
  provisionedUsers: User[]; // we will fill this from Stage 3 completion
  /** Set on websocket "complete" , authoritative success count (may differ from streamed rows on failure) */
  provisionedUsersSuccessCount?: number;
  attemptedUsersCount?: number;
  syncErrors?: CanvasSyncErrorItem[];
}

const CANVAS_SYNC_ERROR_CATEGORY: Record<CanvasSyncErrorCode, string> = {
  DUPLICATE_DIGIT_ASSIGNMENT: "Duplicate assignment",
  TIMEOUT: "Timeout",
  CONNECTIVITY: "Connectivity",
  CANVAS_API: "Canvas API",
  MISSING_PROFILE: "User profile",
  STAGE_FAILED: "Sync stage",
  OTHER: "Other",
};

interface IProvisionedClass {
  id: any;
  class_id: string;
  subject: string;
  period: string;
  start_date: string;
  end_date: string;
}

interface IProvisionedAssignment {
  id: any;
  name: string;
  digit_asset_id: string;
  digit_asset_type: string;
  class_id: string;
  canvas_assignment_id: string;
  due_date: string;
}

interface IProvisionedUser {
  id: any;
  user_id: string;
  email: string;
  name: string;
  class_id: string;
  role: string;
}

function toSyncResultFromInitial(initial: InitialSyncResponse): ICanvasSyncResult {
  return {
    provisioningStatus: initial.provisioningStatus,
    provisionedClasses: initial.provisionedClasses ?? [],
    provisionedAssignments: initial.provisionedAssignments ?? [],
    provisionedExams: initial.provisionedExams ?? [],
    usersToSync: 0,
    provisionedUsers: [],
    syncErrors: initial.syncErrors,
  };
}

export const CanvasSync = () => {
  const classes = useStyles();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const qCode = queryParams.get("code");
  const qState = queryParams.get("state");

  const [loading, setLoading] = useState<boolean>(false);
  const [hasSynced, setHasSynced] = useState<boolean>(false);
  const [syncResult, setSyncResult] = useState<ICanvasSyncResult | null>(null);
  const [syncResultModalOpen, setSyncResultModalOpen] = useState(false);
  const [syncResultModalTab, setSyncResultModalTab] = useState<"1" | "2" | "3" | "4">(
    "1"
  );
  const [authCode] = useState<string | null>(qCode);
  const [authState] = useState<string | null>(qState);
  const [ltik] = useState(MacsAuthenticator.authHook.ltik ?? "");

  const wsContext = useContext(WebsocketContext) as IWebsocketContext;

  const { clearCanvasSyncData } = wsContext;

  const provisionedClasses = useMemo<IProvisionedClass[]>(() => {
    const result =
      syncResult?.provisionedClasses?.map((item) => {
        return {
          id: item.id,
          class_id: `${item.id}`,
          subject: `${(item as any).subject ?? ""}`,
          period: `${(item as any).period_block ?? ""}`,
          start_date: `${(item as any).start_date ?? ""}`,
          end_date: `${(item as any).end_date ?? ""}`,
        } as IProvisionedClass;
      }) ?? [];

    return result;
  }, [syncResult]);

  const provisionedUsers = useMemo<IProvisionedUser[]>(() => {
    return (
      syncResult?.provisionedUsers?.map((u, index) => {
        return {
          id: index,
          user_id: `${u.id}`,
          email: `${u.email ?? ""}`,
          name: `${(u as any).full_name ?? ""}`,
          class_id: "",
          role: `${u.role ?? ""}`,
        } as IProvisionedUser;
      }) ?? []
    );
  }, [syncResult]);

  const provisionedAssignments = useMemo<IProvisionedAssignment[]>(() => {
    return (
      syncResult?.provisionedAssignments?.map((item, index) => {
        return {
          id: (item as any).assignment_metadata?.canvasMetadata?.id ?? `${index}`,
          name: (item as any).assignment_metadata?.canvasMetadata?.name ?? "",
          digit_asset_id: `${(item as any).digit_resource_id ?? ""}`,
          digit_asset_type: `${(item as any).digit_resource_type ?? ""}`,
          class_id: `${(item as any).class_id ?? ""}`,
          canvas_assignment_id: `${(item as any).lti_resource_id ?? ""}`,
          due_date: `${(item as any).assignment_metadata?.dueDateCheck ?? ""}`,
        } as IProvisionedAssignment;
      }) ?? []
    );
  }, [syncResult]);

  const provisionedExams = useMemo<IProvisionedAssignment[]>(() => {
    return (
      syncResult?.provisionedExams?.map((item, index) => {
        return {
          id: (item as any).assignment_metadata?.canvasMetadata?.id ?? `exam-${index}`,
          name: (item as any).assignment_metadata?.canvasMetadata?.name ?? "",
          digit_asset_id: `${(item as any).digit_resource_id ?? ""}`,
          digit_asset_type: `${(item as any).digit_resource_type ?? ""}`,
          class_id: `${(item as any).class_id ?? ""}`,
          canvas_assignment_id: `${(item as any).lti_resource_id ?? ""}`,
          due_date: `${(item as any).assignment_metadata?.dueDateCheck ?? ""}`,
        } as IProvisionedAssignment;
      }) ?? []
    );
  }, [syncResult]);

  const authenticateCanvas = useCallback(async () => {
    try {
      const response = await axiosJWT.get<
        ICanvasTokenRedirectParams | InitialSyncResponse
      >("/oAuth2/canvas", {
        params: {
          ltik: ltik,
          ...(authCode && { canvasCode: authCode, canvasState: authState }),
        },
      });

      if (instanceOfRedirect(response.data)) {
        const { client_id, response_type, redirect_uri, state, auth_uri, scope } =
          response.data;

        if (client_id && response_type && redirect_uri) {
          const url = new URL(auth_uri);
          url.search = new URLSearchParams({
            client_id,
            response_type,
            state,
            scopes: scope.join(" "),
            redirect_uri: `${window.location.origin}${redirect_uri}`,
          }).toString();

          window.location.href = url.toString();
        }
        return;
      }

      if (instanceOfInitialSyncResponse(response.data)) {
        const next = toSyncResultFromInitial(response.data);
        clearCanvasSyncData();
        setSyncResult(next);

        // We’ve started the process; Stage 2/3 will stream. Treat as “hasSynced started”.
        setHasSynced(true);
        return;
      }

      console.warn("Unexpected response from Canvas sync endpoint:", response.data);
      ErrorToast("Unexpected response from Canvas sync endpoint");
      setHasSynced(false);
    } catch (e) {
      console.log(e);
      setHasSynced(false);
      ErrorToast("Failed to start Canvas sync");
    }
  }, [authCode, authState, ltik, clearCanvasSyncData]);

  useEffect(() => {
    const wsData = wsContext.canvasSyncData;
    if (!wsData) return;

    // Log terminal user-sync failures only (not per-user stage noise)
    if (wsData.userSyncFinished && wsData.provisioningStatus === "failure") {
      console.error(wsData.error);
    }

    setSyncResult((prev) => {
      const base: ICanvasSyncResult =
        prev ??
        ({
          provisioningStatus: "inprogress",
          provisionedClasses: [],
          provisionedAssignments: [],
          provisionedExams: [],
          usersToSync: 0,
          provisionedUsers: [],
        } as ICanvasSyncResult);

      const usersToSync = wsData.activeUsersCount ?? base.usersToSync;
      const userSyncFinished = wsData.userSyncFinished === true;

      if (!userSyncFinished) {
        return {
          ...base,
          provisioningStatus: "inprogress",
          usersToSync,
          syncErrors: wsData.syncErrors ?? base.syncErrors,
        };
      }

      return {
        ...base,
        provisioningStatus: wsData.provisioningStatus ?? base.provisioningStatus,
        usersToSync,
        provisionedUsers: Array.isArray(wsData.users)
          ? wsData.users
          : base.provisionedUsers,
        provisionedUsersSuccessCount:
          typeof wsData.provisionedUsersSuccessCount === "number"
            ? wsData.provisionedUsersSuccessCount
            : base.provisionedUsersSuccessCount,
        attemptedUsersCount:
          typeof wsData.attemptedUsersCount === "number"
            ? wsData.attemptedUsersCount
            : base.attemptedUsersCount,
        syncErrors: wsData.syncErrors ?? base.syncErrors,
      };
    });

    // Mark “done” only when user sync completes successfully
    if (wsData.userSyncFinished && wsData.provisioningStatus === "success") {
      setHasSynced(true);
    }
  }, [wsContext.canvasSyncData]);

  useEffect(() => {
    if (authCode && authState && !hasSynced) {
      setLoading(true);
      authenticateCanvas().finally(() => setLoading(false));
    }
  }, [hasSynced, authCode, authState, authenticateCanvas]);

  const handleClickSync = async () => {
    if (!hasSynced) {
      setLoading(true);
      authenticateCanvas().finally(() => setLoading(false));
    }
  };

  const usersToSyncMessage = useMemo(() => {
    const status = syncResult?.provisioningStatus ?? "inprogress";
    const target = syncResult?.usersToSync ?? 0;
    const successCount =
      syncResult?.provisionedUsersSuccessCount ??
      syncResult?.provisionedUsers?.length ??
      0;
    const syncErrors = syncResult?.syncErrors ?? [];
    const singleErrorMessage =
      syncErrors.length === 1 ? syncErrors[0]?.message ?? "" : "";

    switch (status) {
      case "inprogress":
        return "You can leave this page, it will keep going.";
      case "success": {
        const n = successCount;
        return `${n} user${n === 1 ? "" : "s"} successfully synced.`;
      }
      case "failure":
        // UX: if there's exactly one clear error, show it inline on the sync page.
        if (singleErrorMessage) return singleErrorMessage;
        return `Sync could not be completed${
          target > 0 ? ` (${target} user${target === 1 ? "" : "s"}).` : "."
        } Open View Details → Errors for more information.`;
      default:
        return "";
    }
  }, [
    syncResult?.provisionedUsers.length,
    syncResult?.provisionedUsersSuccessCount,
    syncResult?.provisioningStatus,
    syncResult?.syncErrors,
    syncResult?.usersToSync,
  ]);

  const usersTabLabel = useMemo(() => {
    const status = syncResult?.provisioningStatus ?? "inprogress";
    const target = syncResult?.usersToSync ?? 0;
    const done = syncResult?.provisionedUsers?.length ?? 0;

    if (status === "inprogress") return `Users (Syncing ${done}/${target || "?"})`;
    if (status === "failure") return `Users (Failed)`;
    return `Users`;
  }, [
    syncResult?.provisioningStatus,
    syncResult?.usersToSync,
    syncResult?.provisionedUsers.length,
  ]);

  const canOpenDetails = !!syncResult;
  const syncErrors = syncResult?.syncErrors ?? [];
  const hasSyncErrors = syncErrors.length > 0;
  const hasSingleSyncError = syncErrors.length === 1;
  const hasMultipleSyncErrors = syncErrors.length > 1;
  const errorsTabLabel = hasSyncErrors ? `Errors (${syncErrors.length})` : "Errors";

  const openSyncDetails = (preferErrorsTab?: boolean) => {
    setSyncResultModalOpen(true);
    if (preferErrorsTab && hasSyncErrors) {
      setSyncResultModalTab("4");
    }
  };

  useEffect(() => {
    if (
      syncResultModalOpen &&
      syncResult?.provisioningStatus === "failure" &&
      hasSyncErrors
    ) {
      setSyncResultModalTab("4");
    }
  }, [syncResultModalOpen, syncResult?.provisioningStatus, hasSyncErrors]);

  return (
    <>
      <Modal
        open={syncResultModalOpen}
        onClose={() => setSyncResultModalOpen(false)}
        aria-labelledby="canvas-sync-results-modal">
        <Card
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            display: "flex",
            flexDirection: "column",
            height: "600px",
            width: "600px",
          }}>
          <Tabs
            value={syncResultModalTab}
            onChange={(e, value) => setSyncResultModalTab(value)}>
            <Tab label="Classes" value="1" />
            <Tab label={usersTabLabel} value="2" />
            <Tab label="Assignments" value="3" />
            {(hasSyncErrors || syncResult?.provisioningStatus === "failure") && (
              <Tab
                label={errorsTabLabel}
                value="4"
                sx={{ color: hasSyncErrors ? "error.main" : undefined }}
              />
            )}
          </Tabs>

          <Box sx={{ width: "100%", overflowY: "auto", flexGrow: 1 }}>
            {syncResultModalTab === "1" && (
              <DataGrid
                rows={provisionedClasses}
                paginationMode="client"
                pageSize={100}
                rowsPerPageOptions={[100]}
                pagination
                columns={[
                  {
                    field: "class_id",
                    headerName: "Digit Class ID",
                    type: "text",
                    align: "left",
                    minWidth: 220,
                  },
                  {
                    field: "subject",
                    headerName: "Subject",
                    type: "text",
                    align: "left",
                    minWidth: 220,
                  },
                  {
                    field: "period",
                    headerName: "Period",
                    type: "text",
                    align: "left",
                    minWidth: 220,
                  },
                  {
                    field: "start_date",
                    headerName: "Start Date",
                    type: "text",
                    align: "left",
                    minWidth: 220,
                  },
                  {
                    field: "end_date",
                    headerName: "End Date",
                    type: "text",
                    align: "left",
                    minWidth: 220,
                  },
                ]}
                autoHeight
                disableSelectionOnClick
                disableColumnSelector
                disableDensitySelector
                disableColumnMenu
                loading={loading}
              />
            )}

            {syncResultModalTab === "2" && (
              <DataGrid
                rows={provisionedUsers}
                paginationMode="client"
                pageSize={100}
                rowsPerPageOptions={[100]}
                pagination
                columns={[
                  {
                    field: "name",
                    headerName: "Name",
                    type: "text",
                    align: "left",
                    minWidth: 220,
                  },
                  {
                    field: "email",
                    headerName: "Email",
                    type: "text",
                    align: "left",
                    minWidth: 220,
                  },
                  {
                    field: "role",
                    headerName: "Role",
                    type: "text",
                    align: "left",
                    minWidth: 220,
                  },
                  {
                    field: "class_id",
                    headerName: "Digit Class ID(s)",
                    type: "text",
                    align: "left",
                    minWidth: 220,
                  },
                  {
                    field: "user_id",
                    headerName: "Digit User ID",
                    type: "text",
                    align: "left",
                    minWidth: 220,
                  },
                ]}
                autoHeight
                disableSelectionOnClick
                disableColumnSelector
                disableDensitySelector
                disableColumnMenu
                loading={loading}
              />
            )}

            {syncResultModalTab === "4" && (
              <Stack spacing={2} sx={{ p: 2 }}>
                {hasSyncErrors ? (
                  syncErrors.map((err, index) => (
                    <Alert
                      key={`${err.code}-${index}`}
                      severity="error"
                      variant="outlined">
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {CANVAS_SYNC_ERROR_CATEGORY[err.code] ?? "Error"}
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {err.message}
                      </Typography>
                    </Alert>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No detailed error messages were reported. Check the browser console or
                    contact support if the issue continues.
                  </Typography>
                )}
              </Stack>
            )}

            {syncResultModalTab === "3" && (
              <DataGrid
                rows={[...provisionedAssignments, ...provisionedExams]}
                paginationMode="client"
                pageSize={100}
                rowsPerPageOptions={[100]}
                pagination
                columns={[
                  {
                    field: "name",
                    headerName: "Assignment Name",
                    type: "text",
                    align: "left",
                    minWidth: 220,
                  },
                  {
                    field: "digit_asset_type",
                    headerName: "Digit Asset Type",
                    type: "text",
                    align: "left",
                    minWidth: 220,
                  },
                  {
                    field: "due_date",
                    headerName: "Due Date",
                    type: "text",
                    align: "left",
                    minWidth: 220,
                  },
                  {
                    field: "class_id",
                    headerName: "Digit Class ID",
                    type: "text",
                    align: "left",
                    minWidth: 220,
                  },
                ]}
                autoHeight
                disableSelectionOnClick
                disableColumnSelector
                disableDensitySelector
                disableColumnMenu
                loading={loading}
              />
            )}
          </Box>
        </Card>
      </Modal>

      <Stack minHeight={550} className={classes.root} id="main">
        <BGLeft className={classes.backgroundImage} style={{ left: "0", bottom: "0" }} />
        <BGRight className={classes.backgroundImage} style={{ right: "0", top: "0" }} />

        <Stack
          direction="row"
          justifyContent="center"
          sx={{ height: "100%", zIndex: 10 }}>
          <Stack
            className={classes.container}
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "center" }}>
            <div className={classes.headerContainer}>
              <Typography component="h1" variant="h5" className={classes.header}>
                Let’s get this Canvas course set up!
              </Typography>
              <Typography component="main" variant="body1" className={classes.subHeader}>
                We'll need to sync your course sections, enrollments, and grading settings
                to give you a smooth Digit experience. Please click 'Sync' and authorize
                Digit to access Canvas temporarily on your behalf. We'll handle the rest
                from there!
              </Typography>
            </div>

            <Box
              className={classes.formCardCentered}
              sx={{
                width: "480px",
                height: "auto",
              }}>
              {syncResult &&
              (syncResult.provisionedClasses.length > 0 ||
                syncResult.provisioningStatus === "failure") ? (
                <div>
                  <Typography
                    component="p"
                    className={classes.pad16}
                    color={
                      syncResult.provisioningStatus === "failure"
                        ? "error"
                        : "text.secondary"
                    }>
                    {syncResult.provisioningStatus === "failure" ? (
                      <>
                        Canvas sync was unsuccessful.
                        {hasSingleSyncError ? null : <> {usersToSyncMessage}</>}
                      </>
                    ) : (
                      <>
                        Successfully synced {syncResult.provisionedClasses.length ?? 0}{" "}
                        classes, and {syncResult.provisionedAssignments.length ?? 0}{" "}
                        assignments. {usersToSyncMessage}
                      </>
                    )}
                    {canOpenDetails && (
                      <Link
                        sx={{ display: "block" }}
                        onClick={() =>
                          openSyncDetails(syncResult?.provisioningStatus === "failure")
                        }>
                        View Details
                        {hasMultipleSyncErrors ? " (see Errors tab)" : ""}
                      </Link>
                    )}
                  </Typography>
                  {syncResult.provisioningStatus === "failure" && hasSingleSyncError && (
                    <Alert severity="error" variant="outlined">
                      <Typography variant="body2">{usersToSyncMessage}</Typography>
                    </Alert>
                  )}
                </div>
              ) : authCode ? (
                <div>
                  <Typography
                    component="p"
                    className={classes.pad16}
                    color="text.secondary">
                    One moment while we sync your Canvas course.
                  </Typography>
                  <div className={classes.fieldsContainer}>
                    <LoadingButton
                      variant="contained"
                      color="primary"
                      size="large"
                      sx={{ marginLeft: "auto", height: "48px", borderRadius: "8px" }}
                      disabled
                      loading
                      onClick={() => {}}>
                      Sync
                    </LoadingButton>
                  </div>
                </div>
              ) : (
                <div>
                  <Typography
                    component="p"
                    className={classes.pad16}
                    color="text.secondary">
                    Click Sync and authorize Digit to temporarily access Canvas on your
                    behalf.
                  </Typography>
                  <div className={classes.fieldsContainer}>
                    <LoadingButton
                      variant="contained"
                      color="primary"
                      size="large"
                      sx={{ marginLeft: "auto", height: "48px", borderRadius: "8px" }}
                      disabled={loading || hasSynced}
                      loading={loading}
                      onClick={handleClickSync}>
                      Sync
                    </LoadingButton>
                  </div>
                </div>
              )}
            </Box>
          </Stack>
        </Stack>
      </Stack>
    </>
  );
};
